import { memo } from 'react';
import { Trash2 } from 'lucide-react';

import type { CandidateListItem } from '@/api/schema';
import { Td, Tr } from '@/components/ui/Table';
import { Checkbox } from '@/components/ui/Checkbox';
import { VacancyIconButton } from '@/features/careers/components/VacancyIconButton';
import type { DateFormat } from '@/lib/dates';

import type { CandidateColumn } from '../columns';
import { renderCell } from './CandidateCells';
import styles from './CandidateRow.module.css';

/**
 * One row of the register.
 *
 * Memoised, which is worth doing here and almost nowhere else: at a hundred rows a
 * side and twenty-two columns a re-render is two thousand cells. The props are all
 * referentially stable (`columns` comes from a memo, `dateFormat` is a string, and
 * `onOpen` is a callback), so the comparison actually holds.
 */
export const CandidateRow = memo(function CandidateRow({
  candidate,
  columns,
  dateFormat,
  onOpen,
  selection,
}: {
  candidate: CandidateListItem;
  columns: readonly CandidateColumn[];
  dateFormat: DateFormat;
  /** Opens the record. `Tr` handles the keyboard side once this is present. */
  onOpen: (id: string) => void;
  selection?:
    | {
        checked: boolean;
        disabled: boolean;
        onToggle: (id: string, checked: boolean) => void;
        onDelete: (candidate: CandidateListItem) => void;
      }
    | undefined;
}) {
  return (
    <Tr
      selected={selection?.checked ?? false}
      onClick={() => {
        onOpen(candidate.id);
      }}
    >
      {selection ? (
        <Td
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Checkbox
            aria-label={`Select ${candidate.full_name} (${candidate.reference})`}
            checked={selection.checked}
            disabled={selection.disabled}
            onChange={(event) => {
              selection.onToggle(candidate.id, event.target.checked);
            }}
          />
        </Td>
      ) : null}
      {columns.map((column) => (
        <Td key={column.key} className={column.align === 'end' ? styles.numeric : undefined}>
          {renderCell(column.key, candidate, dateFormat)}
        </Td>
      ))}
      {selection ? (
        <Td
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <VacancyIconButton
            label={`Delete ${candidate.full_name} (${candidate.reference})`}
            danger
            icon={<Trash2 size={16} aria-hidden="true" />}
            disabled={selection.disabled}
            onClick={() => {
              selection.onDelete(candidate);
            }}
          />
        </Td>
      ) : null}
    </Tr>
  );
});
